import React, { forwardRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, X } from 'lucide-react';

interface SelectProps {
  className?: string;
  disabled?: boolean;
  value?: string;
  label?: string;
  error?: boolean;
  onChange?: (value: string) => void;
  options: unknown[];
  defaultValue?: string;
  renderOption: (option: unknown, onClick: (option: string) => void) => React.ReactNode;
}

const SelectComponent: React.ForwardRefRenderFunction<HTMLDivElement, SelectProps> = (
  {
    value = '',
    onChange,
    className = 'row',
    options = [],
    renderOption,
    defaultValue = '',
    disabled = false,
    error = false,
  },
  ref,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(value || '');

  const handleOptionClick = (option: string) => {
    onChange?.(option);
    setSelectedOption(option);
    setIsOpen(false);
  };

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  return (
    <div
      className={cn(
        'flex flex-col justify-center relative w-full cursor-pointer',
        className === 'row' &&
          'min-w-[325px] h-9 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm',
        isOpen && 'border-b-transparent rounded-b-none',
        disabled && 'pointer-events-none opacity-50 cursor-not-allowed',
        error && 'border-destructive',
      )}
      ref={ref}
    >
      <div
        className='flex w-full flex-row justify-between items-center text-sm font-light text-foreground'
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={cn(!selectedOption && !defaultValue && 'text-muted-foreground')}>
          {selectedOption || defaultValue || 'Select an option'}
        </span>
        {!isOpen ? (
          <ChevronDown className='h-4 w-4 text-muted-foreground shrink-0' />
        ) : (
          <X className='h-4 w-4 text-muted-foreground shrink-0' />
        )}
      </div>
      {isOpen && (
        <ul className='p-0 absolute top-full -left-px w-[calc(100%+2px)] border border-input border-t-0 rounded-b-md z-50 bg-popover text-popover-foreground m-0 list-none max-h-[200px] overflow-y-auto shadow-md'>
          {renderOption
            ? options.map((option) => renderOption(option, handleOptionClick))
            : options.map((option) => (
                <li
                  key={String(option)}
                  className='text-left bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground cursor-pointer first:rounded-t-none last:rounded-b-md'
                  onClick={() => handleOptionClick(String(option))}
                >
                  {String(option)}
                </li>
              ))}
        </ul>
      )}
    </div>
  );
};

const Select = forwardRef(SelectComponent);

export { Select };
